// Shared shell for workspace entity CSV bulk-import modals.
import type { ReactNode } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import {
  inputClass,
  mfgPrimaryBtnLgClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";

export function ManufacturingEntityBulkImportModalFrame({
  title,
  formatGuide,
  exampleCsv,
  onDownloadExample,
  text,
  onTextChange,
  onFile,
  previewSummary,
  submitting,
  submitError,
  importableCount,
  onSubmit,
  onClose,
  submitLabel = "Import",
}: {
  title: string;
  formatGuide: ReactNode;
  exampleCsv: string;
  onDownloadExample: () => void;
  text: string;
  onTextChange: (next: string) => void;
  onFile: (file: File) => void;
  previewSummary: ReactNode;
  submitting: boolean;
  submitError: string | null;
  importableCount: number;
  onSubmit: () => void;
  onClose: () => void;
  submitLabel?: string;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center"
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-gray-200 bg-white p-5 shadow-xl dark:border-zinc-700 dark:bg-zinc-900 sm:rounded-2xl">
        <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">{title}</h3>

        <details className="mt-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-800/60">
          <summary className="cursor-pointer font-medium text-gray-800 dark:text-zinc-100">
            Format guide
          </summary>
          <div className="mt-2 space-y-2 text-xs text-gray-700 dark:text-zinc-300">
            {formatGuide}
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className={mfgSecondaryBtnClass} onClick={onDownloadExample}>
                Download example CSV
              </button>
              <span className="text-[11px] text-gray-500">
                ({exampleCsv.split("\n").length - 1} sample data rows)
              </span>
            </div>
          </div>
        </details>

        <div className="mt-4 space-y-3">
          <input
            type="file"
            accept=".csv,.txt,.json,text/csv,text/plain,application/json"
            className="block w-full text-sm text-gray-600 dark:text-zinc-300"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onFile(file);
            }}
          />
          <textarea
            className={`${inputClass} min-h-[10rem] font-mono text-xs`}
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            placeholder="Paste CSV or JSON here…"
            spellCheck={false}
          />
        </div>

        <div className="mt-4">
          <SectionCard title="Preview" description="Validated before submit">
            {previewSummary}
          </SectionCard>
        </div>

        {submitError ? (
          <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
            {submitError}
          </div>
        ) : null}

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button type="button" className={mfgSecondaryBtnClass} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className={mfgPrimaryBtnLgClass}
            onClick={onSubmit}
            disabled={submitting || importableCount === 0}
          >
            {submitting ? "Importing…" : `${submitLabel} (${importableCount})`}
          </button>
        </div>
      </div>
    </div>
  );
}
